import DonatePage from './Pages/DonatePage';
import MonthlyUpsellPage from './Pages/MonthlyUpsellPage';
import CheckoutPage from './Pages/CheckoutPage';
import ct from 'countries-and-timezones';
import locale from 'locale-currency';
import useLanguage from 'FlowBuilder/hooks/useLanguageFlow';
import getNavigatorLocale from 'FlowBuilder/lib/localeHelper';
import { useAct } from 'FlowTaker/store';


function getLikelyCurrency(availableCurrencies) {
  // First attempt: Use timezone (more reliable)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const country = ct.getCountryForTimezone(timezone);

  if (country && country.id) {
    // country.id gives us the ISO country code (e.g., 'GB', 'US')
    const timezoneCurrency = locale.getCurrency(country.id);

    if (timezoneCurrency && availableCurrencies.includes(timezoneCurrency.toLowerCase())) {
      return timezoneCurrency.toLowerCase();
    }
  }

  // Second attempt: Fall back to your existing locale-based detection
  const userCurrency = locale.getCurrency(getNavigatorLocale());

  if (userCurrency && availableCurrencies.includes(userCurrency.toLowerCase())) {
    return userCurrency.toLowerCase();
  }

  // Final fallback: return first available currency
  return availableCurrencies[0];
}

export default function DonateForm({ action, onSubmit }) {
  const { config } = action
  const act = useAct()

  const [showCheckout, setShowCheckout] = useState(false);
  const [isMonthly, setIsMonthly] = useState(!!config.disableOneOffDonations);
  const [page, setPage] = useState('donate');
  const [coverFee, setCoverFee] = useState(false);
  const [currency, setCurrency] = useState(getLikelyCurrency(config.currencies || ['gbp']));
  const [overridePresetAmounts, setOverridePresetAmounts] = useState(null);
  const defaultAmount = parseFloat((config.defaultAmounts || {})[currency] || '0');
  const fundraisingFees = useSel(s => (s.builder || s.taker).flow.fundraisingFees)
  const isPreview = useSel(s => s.local.isPreview)
  const [amount, setAmount] = useState(defaultAmount);
  const language = useLanguage();
  const stripePromise = act.taker.getStripePromise(language);

  const presetAmounts = overridePresetAmounts || (config.amounts || {})[currency] || [];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    const amountParam = urlParams.get('amount')
    if (amountParam && !isNaN(parseFloat(amountParam))) {
      setAmount((parseFloat(amountParam) * 100))
    }

    const amountsParams = urlParams.getAll('amounts[]')
    if (amountsParams && amountsParams.length > 0) {
      const parsedAmounts = amountsParams
        .map(amount => parseFloat(amount) * 100)
        .filter(amount => !isNaN(amount))

      if (parsedAmounts.length > 0) {
        setOverridePresetAmounts(parsedAmounts)
      }
    }
  }, [])

  useEffect(() => {
    if (config.coverFee && config.preTickCoverFee) {
      setCoverFee(true);
    }
  }, [config.coverFee, config.preTickCoverFee]);

  const { paymentAmount, coverFeeAmount } = useMemo(() => {
    const coverFeeAmount = Math.round(
      (amount + fundraisingFees.fixed) / (1 - (fundraisingFees.rate)) - amount
    );

    if (coverFee) {
      return { paymentAmount: amount + coverFeeAmount, coverFeeAmount }
    } else {
      return { paymentAmount: amount, coverFeeAmount }
    }
  }, [amount, coverFee]);

  function handleSubmit() {
    if (page === 'donate' && !isMonthly && config.enableMonthlyDonations && config.enableMonthlyUpsell) {
      setPage('upsell');
    } else {
      handleStartCheckout();
    }
  }

  function handleStartCheckout() {
    if (!isPreview) {
      setShowCheckout(true);
    } else {
      // In preview mode, just show what would happen
      console.log('Preview mode - would donate:', amount, isMonthly ? 'monthly' : 'one-off');
    }
  }

  function displayAmount(value) {
    const formatter = new Intl.NumberFormat(getNavigatorLocale(), {
      style: 'currency',
      currency: currency.toUpperCase()
    })

    return value % 100 === 0 ?
      formatter.format(value / 100).replace('.00', '') :
      formatter.format(value / 100);
  }

  return (
    <div className="donation-form">
      {!showCheckout && page === 'donate' && (
        <DonatePage
          actionId={action.id}
          config={config}
          isMonthly={isMonthly}
          setIsMonthly={setIsMonthly}
          amount={amount}
          setAmount={setAmount}
          coverFee={coverFee}
          setCoverFee={setCoverFee}
          currency={currency}
          setCurrency={setCurrency}
          displayAmount={displayAmount}
          coverFeeAmount={coverFeeAmount}
          paymentAmount={paymentAmount}
          handleSubmit={handleSubmit}
          presetAmounts={presetAmounts}
          stripePromise={stripePromise}
          onComplete={onSubmit}
        />
      )}

      {!showCheckout && page === 'upsell' && (
        <MonthlyUpsellPage
          actionId={action.id}
          config={config}
          amount={paymentAmount}
          setMonthly={setIsMonthly}
          handleSubmit={handleSubmit}
          displayAmount={displayAmount}
          coverFee={coverFee}
          setCoverFee={setCoverFee}
          coverFeeAmount={coverFeeAmount}
          handleBack={() => setPage('donate')}
        />
      )}

      {showCheckout && (
        <CheckoutPage
          amountInMinorUnits={amount}
          isMonthly={isMonthly}
          currency={currency}
          coverFee={coverFee}
          onComplete={onSubmit}
          stripePromise={stripePromise}
          handleBack={() => { setShowCheckout(false); setPage('donate'); }}
        />
      )}
    </div>
  );
}

